/**
 * Verticaly フォームバリデーションシステム v1.0
 * リアルタイムエラー表示、フォーカス管理、ユーザビリティ向上
 */

(function() {
  'use strict';

  // バリデーションルール定義
  const validationRules = {
    email: {
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: '有効なメールアドレスを入力してください'
    },
    phone: {
      pattern: /^[0-9\-\+\(\)\s]+$/,
      message: '有効な電話番号を入力してください（数字、ハイフン、括弧のみ）'
    },
    zip: {
      pattern: /^\d{3}-?\d{4}$/,
      message: '郵便番号は7桁の数字（例: 123-4567）で入力してください'
    },
    required: {
      message: 'この項目は必須です'
    }
  };

  // スタイル追加
  if (!document.querySelector('#v0-validation-styles')) {
    const style = document.createElement('style');
    style.id = 'v0-validation-styles';
    style.textContent = `
      .v0-input-error {
        border-color: #ef4444 !important;
        background-color: #fef2f2 !important;
      }

      .v0-input-success {
        border-color: #22c55e !important;
      }

      .v0-error-message {
        display: flex;
        align-items: center;
        gap: 6px;
        color: #dc2626;
        font-size: 12px;
        margin-top: 4px;
        animation: v0-errorSlideIn 0.2s ease-out;
      }

      .v0-error-message svg {
        flex-shrink: 0;
      }

      @keyframes v0-errorSlideIn {
        from {
          opacity: 0;
          transform: translateY(-4px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      .v0-input-group {
        position: relative;
      }

      .v0-input-group .v0-error-message {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * バリデーションシステム
   */
  window.V0Validation = {
    /**
     * 入力フィールドをバリデート
     * @param {HTMLInputElement} input - 検証する入力要素
     * @param {boolean} showError - エラーメッセージを表示するか
     * @returns {boolean} - バリデーション成功/失敗
     */
    validateField: function(input, showError = true) {
      if (!input) return true;

      const value = input.value.trim();
      const inputGroup = input.closest('.v0-input-group') || input.parentElement;
      let isValid = true;
      let errorMessage = '';

      // 既存のエラーメッセージを削除
      this.clearError(input);

      // Required チェック
      if (input.hasAttribute('required') && !value) {
        isValid = false;
        errorMessage = validationRules.required.message;
      }

      // Type別バリデーション
      if (value && isValid) {
        const type = input.getAttribute('type');
        const inputName = input.getAttribute('name');

        if (type === 'email' || inputName === 'email' || inputName === 'customer[email]') {
          if (!validationRules.email.pattern.test(value)) {
            isValid = false;
            errorMessage = validationRules.email.message;
          }
        }

        if (type === 'tel' || inputName === 'phone') {
          if (!validationRules.phone.pattern.test(value)) {
            isValid = false;
            errorMessage = validationRules.phone.message;
          }
        }

        if (inputName === 'zip' || input.classList.contains('v0-zip')) {
          if (!validationRules.zip.pattern.test(value)) {
            isValid = false;
            errorMessage = validationRules.zip.message;
          }
        }
      }

      // カスタムバリデーション（data-validate属性）
      const customValidate = input.getAttribute('data-validate');
      if (customValidate && value && isValid) {
        try {
          const validateFn = new Function('value', customValidate);
          const result = validateFn(value);
          if (result !== true) {
            isValid = false;
            errorMessage = typeof result === 'string' ? result : 'バリデーションエラー';
          }
        } catch (e) {
          console.error('Custom validation error:', e);
        }
      }

      // UI更新
      if (isValid) {
        input.classList.remove('v0-input-error');
        if (value) {
          input.classList.add('v0-input-success');
        }
      } else {
        input.classList.remove('v0-input-success');
        input.classList.add('v0-input-error');

        if (showError && errorMessage) {
          this.showError(input, errorMessage);
        }
      }

      return isValid;
    },

    /**
     * エラーメッセージを表示
     * @param {HTMLInputElement} input - 入力要素
     * @param {string} message - エラーメッセージ
     */
    showError: function(input, message) {
      const inputGroup = input.closest('.v0-input-group') || input.parentElement;
      if (!inputGroup) return;

      const errorDiv = document.createElement('div');
      errorDiv.className = 'v0-error-message';
      errorDiv.innerHTML = `
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>
        <span>${message}</span>
      `;

      inputGroup.appendChild(errorDiv);
    },

    /**
     * エラーメッセージをクリア
     * @param {HTMLInputElement} input - 入力要素
     */
    clearError: function(input) {
      const inputGroup = input.closest('.v0-input-group') || input.parentElement;
      if (!inputGroup) return;

      const existingError = inputGroup.querySelector('.v0-error-message');
      if (existingError) {
        existingError.remove();
      }
    },

    /**
     * フォーム全体をバリデート
     * @param {HTMLFormElement} form - フォーム要素
     * @param {boolean} showErrors - エラーメッセージを表示するか
     * @returns {boolean} - バリデーション成功/失敗
     */
    validateForm: function(form, showErrors = true) {
      if (!form) return true;

      const inputs = form.querySelectorAll('input[required], input[type="email"], input[type="tel"], input[name*="email"], input[name="phone"], input[name="zip"], input.v0-zip');
      let isValid = true;
      let firstInvalidInput = null;

      inputs.forEach(input => {
        if (!this.validateField(input, showErrors)) {
          isValid = false;
          if (!firstInvalidInput) {
            firstInvalidInput = input;
          }
        }
      });

      // 最初のエラーフィールドにフォーカス
      if (!isValid && firstInvalidInput && showErrors) {
        firstInvalidInput.focus();
        firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      return isValid;
    },

    /**
     * フォームに自動バリデーションを設定
     * @param {HTMLFormElement} form - フォーム要素
     */
    setupForm: function(form) {
      if (!form) return;

      const inputs = form.querySelectorAll('input');

      inputs.forEach(input => {
        // blur時にバリデーション
        input.addEventListener('blur', () => {
          if (input.value.trim()) {
            this.validateField(input, true);
          }
        });

        // input時にエラーをクリア
        input.addEventListener('input', () => {
          if (input.classList.contains('v0-input-error')) {
            this.validateField(input, false);
          }
        });
      });

      // submit時にフォーム全体をバリデート
      form.addEventListener('submit', (e) => {
        if (!this.validateForm(form, true)) {
          e.preventDefault();
          if (window.V0Notification) {
            window.V0Notification.error('入力内容に誤りがあります。赤色のフィールドを確認してください。');
          }
        }
      });
    },

    /**
     * ページ内の全フォームに自動バリデーションを設定
     */
    initAll: function() {
      const forms = document.querySelectorAll('form');
      forms.forEach(form => this.setupForm(form));
    }
  };

  // DOMContentLoaded後に自動初期化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.V0Validation.initAll();
    });
  } else {
    window.V0Validation.initAll();
  }

})();
